import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Info, FileText, Shield, Boxes } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { products } from '../data/mockData';
import { useCart } from '../context/CartContext';
import { useToast } from '../hooks/use-toast';
import StockBadge from '../components/StockBadge';
import ReviewSection from '../components/ReviewSection';
import BulkOrderSection from '../components/BulkOrderSection';
import Seo from '../components/Seo';

// Product IDs that have 10-vial bulk packs on /bulk
const BULK_AVAILABLE_IDS = new Set([1, 2, 3, 25, 26, 34, 10, 15]);

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { addToCart } = useCart();
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1);

  const product = products.find(p => p.id === parseInt(id));

  // Variant selection: URL ?variant= wins, else first variant
  const initialVariant = (() => {
    if (!product?.variants) return null;
    const fromUrl = searchParams.get('variant');
    if (fromUrl && product.variants.find(v => v.key === fromUrl)) return fromUrl;
    return product.variants[0].key;
  })();

  const [selectedVariant, setSelectedVariant] = useState(initialVariant);

  if (!product) {
    return (
      <div className="min-h-screen pt-20 bg-[#0C0A09] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Not Found</h2>
          <Button onClick={() => navigate('/products')}>Back to Products</Button>
        </div>
      </div>
    );
  }

  const activeVariant = product.variants
    ? product.variants.find(v => v.key === selectedVariant) || product.variants[0]
    : null;
  const effectivePrice = product.price + (activeVariant?.priceDelta || 0);

  const handleAddToCart = () => {
    const cartProduct = activeVariant
      ? {
          ...product,
          price: effectivePrice,
          variant: activeVariant.key,
          variantLabel: activeVariant.label,
        }
      : product;
    addToCart(cartProduct, quantity);
    toast({
      title: "Added to cart",
      description: `${quantity}x ${product.name}${activeVariant ? ` (${activeVariant.label.split(' (')[0]})` : ''} added to your cart.`,
    });
  };

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: "#0C0A09" }}>
      <Seo
        path={`/product/${product.id}`}
        title={`${product.name} — Research Peptide | Eman's Peppers`}
        description={(product.description || '').slice(0, 155).replace(/\s+\S*$/, '') + '…'}
        image={product.imageUrl}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "image": product.imageUrl,
          "description": product.description,
          "category": product.category,
          "brand": { "@type": "Brand", "name": "Eman's Peppers" },
          "offers": {
            "@type": "Offer",
            "price": effectivePrice,
            "priceCurrency": "USD",
            "availability": "https://schema.org/InStock",
            "url": `https://emans-peppers.com/product/${product.id}`
          }
        }}
      />
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/products')}
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Image Section */}
          <div>
            <Card className="border border-[#362B25] overflow-hidden">
              <CardContent className="p-8">
                {/* Real Product Image */}
                <div className="bg-gradient-to-br from-[#0C0A09] to-[#1A1513] rounded-lg p-8 mb-6">
                  <img 
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-auto max-w-md mx-auto"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <Shield className="h-4 w-4 mr-1 text-[#D4A373]" />
                      Quality Assured
                    </div>
                    <div className="flex items-center">
                      <Info className="h-4 w-4 mr-1 text-[#D4A373]" />
                      Research Grade
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust & Safety Information */}
            <Card className="border border-[#362B25] mt-6">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <Shield className="h-5 w-5 mr-2 text-[#D4A373]" />
                  Quality & Safety Information
                </h3>
                
                <div className="space-y-4">
                  <div className="bg-teal-50 border-l-4 border-teal-600 p-4 rounded">
                    <p className="text-sm text-teal-900 font-medium mb-2">
                      Pharmaceutical-Grade Standards
                    </p>
                    <p className="text-sm text-teal-800">
                      Our peptides are manufactured following strict quality control protocols in specialized facilities 
                      dedicated to research chemical production.
                    </p>
                  </div>

                  <div className="bg-[#0C0A09] p-4 rounded-lg border border-gray-200">
                    <h4 className="font-semibold text-[#F7F3EE] mb-3">Why Choose CM Peptides?</h4>
                    <ul className="space-y-2 text-sm text-[#B5A79E]">
                      <li className="flex items-start">
                        <span className="text-[#D4A373] mr-2">✓</span>
                        <span>Manufactured in controlled environments using advanced synthesis techniques</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#D4A373] mr-2">✓</span>
                        <span>Proper storage and handling protocols maintained throughout supply chain</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#D4A373] mr-2">✓</span>
                        <span>Trusted by research professionals and institutions worldwide</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[#D4A373] mr-2">✓</span>
                        <span>Commitment to providing consistent, reliable research materials</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-xs text-amber-900 font-medium mb-2">
                      ⚠️ Important Safety Information
                    </p>
                    <p className="text-xs text-amber-800">
                      This product is intended for laboratory research use only. Not for human consumption or therapeutic use. 
                      Handle with appropriate safety equipment in a proper laboratory setting. Keep out of reach of children 
                      and unauthorized personnel.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Product Details Section */}
          <div className="space-y-6">
            <Card className="border border-[#362B25]">
              <CardContent className="p-8">
                <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4">
                  {product.category}
                </span>
                
                <h1 className="text-3xl font-bold text-[#F7F3EE] mb-4">{product.name}</h1>

                {BULK_AVAILABLE_IDS.has(product.id) && (
                  <Link
                    to="/bulk"
                    className="inline-flex items-center gap-2 mb-4 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-[0.15em] uppercase transition-transform hover:scale-[1.03]"
                    style={{
                      backgroundColor: 'rgba(212, 163, 115, 0.15)',
                      color: '#D4A373',
                      border: '1px solid rgba(212, 163, 115, 0.5)',
                    }}
                    data-testid={`bulk-available-badge-${product.id}`}
                  >
                    <Boxes className="h-3.5 w-3.5" strokeWidth={2} />
                    Bulk Available — 10 Vial Packs
                  </Link>
                )}

                {product.stockStatus && product.stockStatus !== 'in_stock' && (
                  <div className="mb-4">
                    <StockBadge status={product.stockStatus} count={product.stockCount} note={product.restockNote} size="md" />
                  </div>
                )}

                <div className="flex items-baseline space-x-2 mb-6 flex-wrap gap-y-2">
                  <span className="text-4xl font-bold text-[#D4A373]">${effectivePrice}</span>
                  <span className="text-gray-500">per vial</span>
                  {activeVariant && activeVariant.priceDelta < 0 && (
                    <>
                      <span className="text-xl line-through ml-2" style={{ color: '#5A4A3F' }}>
                        ${product.price}
                      </span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        style={{
                          background: 'linear-gradient(135deg, #86EFAC 0%, #4ADE80 100%)',
                          color: '#0C0A09',
                        }}
                      >
                        Save ${Math.abs(activeVariant.priceDelta)}
                      </span>
                    </>
                  )}
                  {product.onSale && product.originalPrice && !activeVariant && (
                    <>
                      <span className="text-xl line-through ml-2" style={{ color: '#5A4A3F' }}>
                        ${product.originalPrice}
                      </span>
                      <span
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'rgba(220, 38, 38, 0.15)',
                          color: '#FCA5A5',
                          border: '1px solid rgba(220, 38, 38, 0.4)',
                        }}
                      >
                        Flash Sale · Save ${product.originalPrice - product.price}
                      </span>
                    </>
                  )}
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded mb-6">
                  <p className="text-sm text-amber-900 font-medium">
                    <Info className="inline h-4 w-4 mr-1" />
                    For Research Purposes Only - Not for Human Consumption
                  </p>
                </div>

                {product.isBundle && product.bundleContents && (
                  <div
                    className="rounded-lg overflow-hidden mb-6"
                    style={{
                      backgroundColor: '#1A1513',
                      border: '2px solid rgba(212, 163, 115, 0.5)',
                      boxShadow: '0 0 24px rgba(212, 163, 115, 0.15)',
                    }}
                  >
                    <div
                      className="px-4 py-3 flex items-center justify-between gap-4"
                      style={{ background: 'linear-gradient(135deg, #D4A373 0%, #C78C50 100%)' }}
                    >
                      <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#0C0A09' }}>
                        🎁 Bundle Contents
                      </p>
                      {product.originalPrice && product.originalPrice > product.price && (
                        <span
                          className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded"
                          style={{ backgroundColor: '#0C0A09', color: '#FCD34D' }}
                        >
                          Save ${product.originalPrice - product.price}
                        </span>
                      )}
                    </div>
                    <div className="p-4 space-y-2.5">
                      {product.bundleContents.map((item, idx) => (
                        <div
                          key={idx}
                          className="flex items-start justify-between gap-3 py-2"
                          style={{ borderBottom: idx < product.bundleContents.length - 1 ? '1px solid rgba(212, 163, 115, 0.12)' : 'none' }}
                        >
                          <div className="flex items-start gap-3 flex-1">
                            <div
                              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold mt-0.5"
                              style={{ backgroundColor: 'rgba(212, 163, 115, 0.15)', color: '#D4A373', border: '1px solid rgba(212, 163, 115, 0.4)' }}
                            >
                              ✓
                            </div>
                            <p className="text-sm leading-relaxed font-medium" style={{ color: '#F7F3EE' }}>
                              {item.name}
                            </p>
                          </div>
                          <span
                            className="text-xs font-semibold uppercase tracking-wider flex-shrink-0 mt-1.5"
                            style={{ color: '#D4A373' }}
                          >
                            {item.qty}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {product.category === 'Nasal Kits' && (
                  <div
                    className="rounded-lg overflow-hidden mb-6"
                    style={{
                      backgroundColor: '#1A1513',
                      border: '1px solid rgba(212, 163, 115, 0.4)',
                    }}
                  >
                    <div
                      className="px-4 py-3 text-center"
                      style={{ background: 'linear-gradient(135deg, #D4A373 0%, #C78C50 100%)' }}
                    >
                      <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#0C0A09' }}>
                        What's in this kit
                      </p>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-3">
                        <span className="text-lg leading-none mt-0.5">💧</span>
                        <p className="text-sm leading-relaxed" style={{ color: '#F7F3EE' }}>
                          1× Premium amber-glass nasal spray bottle with fine-mist atomizer
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-lg leading-none mt-0.5">🧪</span>
                        <p className="text-sm leading-relaxed" style={{ color: '#F7F3EE' }}>
                          1× Vial of high-purity {product.name.replace(' Nasal Kit', '')} peptide
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <span className="text-lg leading-none mt-0.5">🧴</span>
                        <p className="text-sm leading-relaxed" style={{ color: '#F7F3EE' }}>
                          1× 5ml vial of pharmaceutical-grade sterile saline
                        </p>
                      </div>

                      <div
                        className="rounded p-3 mt-2"
                        style={{ backgroundColor: 'rgba(34, 197, 94, 0.08)', border: '1px solid rgba(34, 197, 94, 0.3)' }}
                      >
                        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#86EFAC' }}>
                          ✓ Local Meetup
                        </p>
                        <p className="text-sm leading-relaxed" style={{ color: '#F7F3EE' }}>
                          Arrives <strong>fully assembled and ready to use</strong> — we mix it for you.
                        </p>
                      </div>

                      <div
                        className="rounded p-3"
                        style={{ backgroundColor: 'rgba(212, 163, 115, 0.08)', border: '1px solid rgba(212, 163, 115, 0.3)' }}
                      >
                        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#D4A373' }}>
                          📦 Shipping
                        </p>
                        <p className="text-sm leading-relaxed" style={{ color: '#F7F3EE' }}>
                          Ships as <strong>3 separate components</strong> for safe transit. You'll combine the saline with the peptide using the included bottle, then attach the spray pump (takes ~30 seconds).
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Variant Selector */}
                {product.variants && (
                  <div className="mb-6" data-testid="variant-selector">
                    <label className="block text-xs uppercase tracking-widest font-bold text-[#B5A79E] mb-3">
                      Choose Format
                    </label>
                    <div className="grid grid-cols-1 gap-2">
                      {product.variants.map((v) => {
                        const isActive = v.key === selectedVariant;
                        const priceForVariant = product.price + v.priceDelta;
                        return (
                          <button
                            key={v.key}
                            type="button"
                            onClick={() => setSelectedVariant(v.key)}
                            data-testid={`variant-${v.key}`}
                            className="text-left p-3 rounded-md transition-all"
                            style={{
                              backgroundColor: isActive ? 'rgba(212, 163, 115, 0.12)' : '#0C0A09',
                              border: isActive
                                ? '2px solid #D4A373'
                                : '1px solid #362B25',
                              cursor: 'pointer',
                            }}
                          >
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold" style={{ color: '#F7F3EE' }}>
                                    {v.label}
                                  </span>
                                  {v.priceDelta < 0 && (
                                    <span
                                      className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded"
                                      style={{
                                        background: 'linear-gradient(135deg, #86EFAC 0%, #4ADE80 100%)',
                                        color: '#0C0A09',
                                      }}
                                    >
                                      Save ${Math.abs(v.priceDelta)}
                                    </span>
                                  )}
                                  {v.key === 'premixed' && (
                                    <span
                                      className="text-[9px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded"
                                      style={{
                                        backgroundColor: 'rgba(212, 163, 115, 0.15)',
                                        border: '1px solid rgba(212, 163, 115, 0.4)',
                                        color: '#D4A373',
                                      }}
                                    >
                                      NEW · Permanent
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs mt-0.5" style={{ color: '#B5A79E' }}>
                                  {v.key === 'premixed'
                                    ? 'Ready to inject — no mixing required. Ships with cold pack.'
                                    : 'Freeze-dried powder + Bac Water sold separately.'}
                                </p>
                              </div>
                              <span
                                className="text-lg font-bold"
                                style={{ color: isActive ? '#D4A373' : '#F7F3EE' }}
                              >
                                ${priceForVariant}
                              </span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Quantity Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#B5A79E] mb-2">
                    Quantity
                  </label>
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      disabled={product.stockStatus === 'out_of_stock'}
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12"
                    >
                      -
                    </Button>
                    <span className="text-2xl font-semibold w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      disabled={product.stockStatus === 'out_of_stock'}
                      onClick={() => setQuantity(quantity + 1)}
                      className="w-12 h-12"
                    >
                      +
                    </Button>
                  </div>
                </div>

                {product.stockStatus === 'out_of_stock' ? (
                  <Button
                    disabled
                    data-testid="oos-add-to-cart-detail"
                    className="w-full h-14 text-lg mb-4 cursor-not-allowed"
                    style={{
                      backgroundColor: '#1A1513',
                      color: '#5A4A3F',
                      border: '1px solid #362B25',
                    }}
                  >
                    {product.restockNote
                      ? `Sold Out — ${product.restockNote}`
                      : 'Sold Out — Restock Coming Soon'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleAddToCart}
                    className="w-full bg-[#D4A373] hover:bg-[#E6CC98] text-[#0C0A09] h-14 text-lg mb-4"
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart - ${(effectivePrice * quantity).toFixed(2)}
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={() => navigate('/products')}
                  className="w-full h-12"
                >
                  Continue Shopping
                </Button>
              </CardContent>
            </Card>

            {/* Product Information */}
            <Card className="border border-[#362B25]">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-[#F7F3EE] mb-4">Description</h3>
                <p className="text-[#B5A79E] mb-6 leading-relaxed">{product.description}</p>

                <h3 className="text-xl font-bold text-[#F7F3EE] mb-4">Benefits</h3>
                <ul className="space-y-2 mb-6">
                  {product.benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-[#D4A373] mr-2">✓</span>
                      <span className="text-[#B5A79E]">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="text-xl font-bold text-[#F7F3EE] mb-4">Usage Instructions</h3>
                <p className="text-[#B5A79E] bg-[#0C0A09] p-4 rounded-lg border border-gray-200">
                  {product.usage}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Bulk Order */}
        <BulkOrderSection product={product} />

        {/* Reviews */}
        <ReviewSection productId={product.id} />
      </div>
    </div>
  );
};

export default ProductDetail;
